import { createCRUDHook } from './createCRUDHook';
import {
  CustomerOrderShipmentDto,
  FilterOptionModel,
  TrackingNumberDto,
  completedShipmentCustomerModel,
  completedShipmentModel,
  UpsShipmentRequestDto,
  UPSCancelResponseDto,
  LTLCancelResponseDto,
  LtlShipmentChargeDto,
  UpsShipmentChargeDto,
  LtlBookingResponseDto,
  UpsBookingResponseDto,
  SpeedShipBookResponseDto,
  SpeedShipBookRequestDto,
  UpsShipmentResponseDto,
  UseEstimateRequestDto,
  UseEstimateResponseDto,
  BookSelectedEstimateRequestDto,
  BookSelectedEstimateResponseDto,
  FilterEstimateResponseDto,
} from 'utils/models';
import {
  FILTERED_ENTITIES,
  GET_COMPLETED_SHIPMENT_BY_CUSTOMER_ID,
  GET_COMPLETED_SHIPMENT_BY_STAFFID,
  GET_ORDER_SHIPMENT_BY_ORDER_ID,
  TBL_CUSTOMER_ORDER_SHIPMENT,
  TBL_CUSTOMER_ORDER_SHIPMENTS_TRACKING_NUMBER,
  GET_UPS_SHIPMENT_CHARGE_FOR_ORDER,
  GET_LTL_SHIPMENT_CHARGE_FOR_ORDER,
  SHIPMENT,
  UPS_SHIP,
  SPEEDSHIP_BOOK,
  SPEEDSHIP_BOOK_CANCEL,
  UPS,
  VOID,
  USE_ESTIMATE,
  BOOK_SELECTED_ESTIMATE,
  SELECTED_ESTIMATE,
  TBL_CUSTOMER_ORDER_SHIPPING_ESTIMATE_SELECTION,
} from './apiEndPoints';
import { prepareQueryParams } from 'utils';

interface CustomerOrderShipmentCustomOperations {
  addCustomerOrderShipmentTrackingNumber: (
    param: TrackingNumberDto,
  ) => Promise<CustomerOrderShipmentDto>;

  getTrackingNumberByFilteredEntities: (
    filteredEntities: FilterOptionModel,
  ) => Promise<TrackingNumberDto>;

  deleteCustomerTrackingNumber: (id: number) => Promise<boolean>;

  getCompletedShipment: (
    completedShipmentEntities: completedShipmentModel,
  ) => Promise<any>;

  GetOrderShipmentByOrderId: (orderId: number) => Promise<any>;

  GetCompletedShipmentByCustomerId: (
    completedShipmentCustomer: completedShipmentCustomerModel,
  ) => Promise<any>;

  GetUpsShipmentChargeForOrder: (
    data: UpsShipmentChargeDto,
  ) => Promise<UpsShipmentResponseDto>;
  GetLtlShipmentChargeForOrder: (
    data: LtlShipmentChargeDto,
  ) => Promise<SpeedShipBookResponseDto>;

  GetUpsShip: (param: UpsShipmentRequestDto) => Promise<UpsBookingResponseDto>;
  GetSpeedShipBook: (
    param: SpeedShipBookRequestDto,
  ) => Promise<LtlBookingResponseDto>;

  CancelSpeedshipBook: (
    id: number,
    orderId: number,
  ) => Promise<LTLCancelResponseDto>;

  deleteCustomerUpsShipment: (
    orderId: number,
    orderBoxRecID: number,
  ) => Promise<UPSCancelResponseDto>;

  addShipmentEstimate: (
    param: UseEstimateRequestDto,
  ) => Promise<UseEstimateResponseDto>;

  deleteSelectedEstimate: (id: number) => Promise<any>;

  bookSelectedEstimate: (
    param: BookSelectedEstimateRequestDto,
  ) => Promise<BookSelectedEstimateResponseDto>;

  GetSelectedEstimateByOrderId: (
    orderId: number,
  ) => Promise<UseEstimateResponseDto>;

  getFilterEstimateDataByFE: (
    filteredEntities: FilterOptionModel,
  ) => Promise<FilterEstimateResponseDto[]>;
}

const { Context, useManager, useConsumer } = createCRUDHook<
  CustomerOrderShipmentDto,
  CustomerOrderShipmentDto,
  CustomerOrderShipmentCustomOperations
>({
  entityName: 'CustomerOrderShipment',
  endpoint: TBL_CUSTOMER_ORDER_SHIPMENT,

  customOperations: (auth) => ({
    addCustomerOrderShipmentTrackingNumber: async (
      param: TrackingNumberDto,
    ) => {
      const res = await auth.makeAuthenticatedPost(
        TBL_CUSTOMER_ORDER_SHIPMENTS_TRACKING_NUMBER,
        param,
      );
      return res.data;
    },

    getTrackingNumberByFilteredEntities: async (
      filteredEntities: FilterOptionModel,
    ) => {
      const res = await auth.makeAuthenticatedGet(
        TBL_CUSTOMER_ORDER_SHIPMENTS_TRACKING_NUMBER +
          FILTERED_ENTITIES +
          `?${prepareQueryParams(filteredEntities)}`,
      );
      return res.data;
    },

    deleteCustomerTrackingNumber: async (id: number) => {
      const res = await auth.makeAuthenticatedDelete(
        `${TBL_CUSTOMER_ORDER_SHIPMENTS_TRACKING_NUMBER}/${id}`,
      );
      return res.data;
    },

    getCompletedShipment: async (
      completedShipmentEntities: completedShipmentModel,
    ) => {
      const res = await auth.makeAuthenticatedGet(
        TBL_CUSTOMER_ORDER_SHIPMENT +
          GET_COMPLETED_SHIPMENT_BY_STAFFID +
          `?${prepareQueryParams(completedShipmentEntities)}`,
      );
      return res.data;
    },

    GetOrderShipmentByOrderId: async (orderId: number) => {
      const res = await auth.makeAuthenticatedGet(
        `${TBL_CUSTOMER_ORDER_SHIPMENT}${GET_ORDER_SHIPMENT_BY_ORDER_ID}?orderId=${orderId}`,
      );
      return res.data;
    },

    GetCompletedShipmentByCustomerId: async (
      completedShipmentCustomer: completedShipmentCustomerModel,
    ) => {
      const res = await auth.makeAuthenticatedGet(
        TBL_CUSTOMER_ORDER_SHIPMENT +
          GET_COMPLETED_SHIPMENT_BY_CUSTOMER_ID +
          `?${prepareQueryParams(completedShipmentCustomer)}`,
      );
      return res.data;
    },

    GetUpsShipmentChargeForOrder: async (data: any) => {
      const res = await auth.makeAuthenticatedPost(
        SHIPMENT + GET_UPS_SHIPMENT_CHARGE_FOR_ORDER,
        data,
      );
      return res.data;
    },

    GetLtlShipmentChargeForOrder: async (data: any) => {
      const res = await auth.makeAuthenticatedPost(
        SHIPMENT + GET_LTL_SHIPMENT_CHARGE_FOR_ORDER,
        data,
      );
      return res.data;
    },

    GetUpsShip: async (param: UpsShipmentRequestDto) => {
      const res = await auth.makeAuthenticatedPost(SHIPMENT + UPS_SHIP, param);
      return res.data;
    },

    GetSpeedShipBook: async (param: SpeedShipBookRequestDto) => {
      const res = await auth.makeAuthenticatedPost(
        SHIPMENT + SPEEDSHIP_BOOK,
        param,
      );
      return res.data;
    },

    CancelSpeedshipBook: async (id: number, orderId: number) => {
      const res = await auth.makeAuthenticatedDelete(
        `${SHIPMENT}${SPEEDSHIP_BOOK_CANCEL}?orderId=${orderId}&orderBoxRecId=${id}`,
      );
      return res.data;
    },

    deleteCustomerUpsShipment: async (
      orderId: number,
      orderBoxRecID: number,
    ) => {
      const res = await auth.makeAuthenticatedDelete(
        `${SHIPMENT}${UPS}${VOID}?orderId=${orderId}&orderBoxRecId=${orderBoxRecID}`,
      );
      return res.data;
    },

    addShipmentEstimate: async (param: UseEstimateRequestDto) => {
      const res = await auth.makeAuthenticatedPost(
        SHIPMENT + USE_ESTIMATE,
        param,
      );
      return res.data;
    },

    deleteSelectedEstimate: async (id: number) => {
      const res = await auth.makeAuthenticatedDelete(
        `${TBL_CUSTOMER_ORDER_SHIPPING_ESTIMATE_SELECTION}/${id}`,
      );
      return res.data;
    },

    bookSelectedEstimate: async (param: BookSelectedEstimateRequestDto) => {
      const res = await auth.makeAuthenticatedPost(
        SHIPMENT + BOOK_SELECTED_ESTIMATE,
        param,
      );
      return res.data;
    },

    GetSelectedEstimateByOrderId: async (orderId: number) => {
      const res = await auth.makeAuthenticatedGet(
        `${SHIPMENT}${SELECTED_ESTIMATE}/${orderId}`,
      );
      return res.data;
    },
    getFilterEstimateDataByFE: async (filterOptions: FilterOptionModel) => {
      const res = await auth.makeAuthenticatedGet(
        TBL_CUSTOMER_ORDER_SHIPPING_ESTIMATE_SELECTION +
          `?${prepareQueryParams(filterOptions)}`,
      );

      return res.data;
    },
  }),
});

export const CustomerOrderShipmentContext = Context;
export const useCustomerOrderShipmentManager = useManager;
export const useCustomerOrderShipment = useConsumer;
